import {
  getPreHydrationSnapshot,
  getPreHydrationSnapshotError,
} from '@aboutyou/lazy-imports';
import {Log} from '@aboutyou/mlm';
import {useCanGoBack} from '@aboutyou/router';
import {useEffect, useRef} from 'react';

/**
 * Logs when a skeleton is shown on a hard landing (no history to go back to),
 * i.e. the user landed on a loading state instead of server-rendered content.
 *
 * The logged attributes attribute the event to its cause:
 * - `ssrServedSkeleton`: the skeleton was already part of the SSR HTML
 *   (server-side data was not ready). Requires the skeleton to render a
 *   `data-ssr-skeleton` attribute whose value equals this hook's `context`.
 * - `ssrClientRenderBoundaries` > 0: the server errored inside Suspense
 *   boundaries and delegated them to the client.
 * - `renderMode`: 'client-render' means SSR output was discarded wholesale
 *   (bot mode, ssr=0, server markup error) — not a hydration problem.
 *
 * `snapshotAvailable` tells whether the attribution above could be captured at
 * all. The attributes are only present when it is `true`, so each one keeps a
 * single type and stays usable as a Datadog facet.
 */
export const useLogHydrationFailed = ({context}: {context: string}) => {
  const canGoBack = useCanGoBack();
  const logged = useRef(false);

  useEffect(() => {
    if (logged.current) {
      return;
    }
    if (!canGoBack) {
      const snapshot = getPreHydrationSnapshot();
      const snapshotError = getPreHydrationSnapshotError();

      Log.error('@aboutyou/logic-components', 'HydrationFailedSkeleton', {
        context,
        snapshotAvailable: Boolean(snapshot),
        ...(snapshot
          ? {
              renderMode: snapshot.renderMode,
              ssrServedSkeleton: snapshot.ssrSkeletons.has(context),
              ssrClientRenderBoundaries: snapshot.ssrClientRenderBoundaries,
              ssrPendingBoundaries: snapshot.ssrPendingBoundaries,
            }
          : {}),
        ...(snapshotError ? {snapshotError} : {}),
      });
      logged.current = true;
    }
  }, [canGoBack, context]);

  return null;
};
